import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import CloseIcon from '@material-ui/icons/Close';
import MobileLayout from '../MobileLayout';
import {
  FulfillmentLineExceptionType,
  WaveItem,
  WaveResponse,
} from '../../../services/fulfillment/types';
import {
  cancelWave,
  completeWave,
  getActiveWave,
  reportPickException,
  scanPick,
  scanQc,
} from '../../../services/fulfillment/waveApi';

const EXCEPTION_LABELS: Record<FulfillmentLineExceptionType, string> = {
  item_not_found: 'Item Not Found',
  item_damaged: 'Item Damaged',
  skip: 'Skip',
  other: 'Other',
};

type PickStep =
  | { name: 'loading' }
  | { name: 'pick_item'; item: WaveItem; itemIndex: number; totalItems: number }
  | { name: 'move_to_qc'; item: WaveItem; itemIndex: number; totalItems: number }
  | { name: 'auto_exception'; item: WaveItem; itemIndex: number; totalItems: number }
  | { name: 'item_done'; item: WaveItem }
  | { name: 'wave_done'; completedOrders: number; exceptionCount: number };

interface LocationState {
  wave?: WaveResponse;
}

const BikeWavePickFlow: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { waveId } = useParams<{ waveId: string }>();

  const [step, setStep] = useState<PickStep>({ name: 'loading' });
  const [wave, setWave] = useState<WaveResponse | null>(location.state?.wave ?? null);
  const [scanInput, setScanInput] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [exceptionModalOpen, setExceptionModalOpen] = useState(false);
  const [
    selectedExceptionType,
    setSelectedExceptionType,
  ] = useState<FulfillmentLineExceptionType | null>(null);
  const [exceptionNotes, setExceptionNotes] = useState('');
  const [submittingException, setSubmittingException] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [altLocationBinCode, setAltLocationBinCode] = useState<string | null>(null);
  const [releaseConfirmOpen, setReleaseConfirmOpen] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [releaseError, setReleaseError] = useState<string | null>(null);

  const deriveStep = useCallback((w: WaveResponse): PickStep => {
    const unpickedItems = w.items.filter(
      i =>
        i.waveOrderStatus !== 'picked' &&
        i.waveOrderStatus !== 'cancelled' &&
        i.lineStatus !== 'quality-check' &&
        i.lineStatus !== 'picked' &&
        i.lineStatus !== 'packed' &&
        i.lineStatus !== 'exception',
    );
    if (unpickedItems.length === 0) {
      const exceptionCount = w.items.filter(i => i.lineStatus === 'exception').length;
      return { name: 'wave_done', completedOrders: w.completedOrders, exceptionCount };
    }
    const item = unpickedItems[0];
    const totalItems = w.items.length;
    const itemIndex = w.items.findIndex(i => i.shopifyLineItemGid === item.shopifyLineItemGid) + 1;
    if (
      !item.binLocations.some(l => l.locationType === 'pick' || l.locationType === 'refurbished')
    ) {
      return { name: 'auto_exception', item, itemIndex, totalItems };
    }
    return { name: 'pick_item', item, itemIndex, totalItems };
  }, []);

  const initDone = useRef(false);
  useEffect(() => {
    if (initDone.current) return;
    initDone.current = true;
    if (wave) {
      setStep(deriveStep(wave));
      return;
    }
    getActiveWave()
      .then(w => {
        if (w) {
          setWave(w);
          setStep(deriveStep(w));
        } else {
          history.push('/mobile/pick');
        }
      })
      .catch(() => history.push('/mobile/pick'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount-only

  const currentLineItemGid = step.name === 'pick_item' ? step.item.shopifyLineItemGid : null;
  useEffect(() => {
    setAltLocationBinCode(null);
  }, [currentLineItemGid]);

  const focusInput = () => {
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleAutoException = async () => {
    if (step.name !== 'auto_exception') return;
    const { item } = step;
    setSubmittingException(true);
    const result = await reportPickException(
      Number(waveId),
      item.shopifyLineItemGid,
      'item_not_found',
      'No pickable inventory location',
    );
    setSubmittingException(false);
    if ('error' in result) {
      setScanError(`Failed to report exception for ${item.sku} — please try again`);
      return;
    }
    const updatedWave = result as WaveResponse;
    setWave(updatedWave);
    const nextStep = deriveStep(updatedWave);
    setStep(nextStep);
    if (nextStep.name === 'pick_item') focusInput();
  };

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const handlePickScan = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || step.name !== 'pick_item') return;
    const value = scanInput.trim();
    setScanInput('');
    setScanError(null);
    if (!value) return focusInput();

    setScanning(true);
    const result = await scanPick(Number(waveId), step.item.shopifyLineItemGid, value);
    setScanning(false);
    if ('error' in result) {
      if (result.error === 'sku_mismatch') {
        setScanError('SKU does not match. Please scan the correct item.');
        triggerShake();
      } else {
        setScanError('Scan failed. Please try again or report an exception.');
      }
      focusInput();
      return;
    }
    // Advance to move_to_qc
    setStep({
      name: 'move_to_qc',
      item: step.item,
      itemIndex: step.itemIndex,
      totalItems: step.totalItems,
    });
    setScanInput('');
    focusInput();
  };

  const handleQcScan = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || step.name !== 'move_to_qc') return;
    const value = scanInput.trim();
    setScanInput('');
    setScanError(null);
    if (!value) return focusInput();

    setScanning(true);
    const result = await scanQc(Number(waveId), step.item.shopifyLineItemGid, value);
    setScanning(false);
    if ('error' in result) {
      setScanError(
        wave?.qcAllowRefurbishedZone
          ? 'Invalid QC bin. Please scan a valid operations or refurbished-goods bin.'
          : 'Invalid QC bin. Please scan a valid operations bin.',
      );
      focusInput();
      return;
    }
    const updatedWave = result as WaveResponse;
    setWave(updatedWave);
    setStep({ name: 'item_done', item: step.item });
  };

  const handleNextItem = () => {
    if (!wave) return;
    setScanError(null);
    const nextStep = deriveStep(wave);
    setStep(nextStep);
    if (nextStep.name === 'pick_item') focusInput();
  };

  const handleOpenException = () => {
    setSelectedExceptionType(null);
    setExceptionNotes('');
    setExceptionModalOpen(true);
  };

  const handleSubmitException = async () => {
    if (step.name !== 'pick_item' || !selectedExceptionType) return;
    setSubmittingException(true);
    const result = await reportPickException(
      Number(waveId),
      step.item.shopifyLineItemGid,
      selectedExceptionType,
      exceptionNotes || null,
    );
    setSubmittingException(false);
    if ('error' in result) {
      setScanError('Failed to submit exception. Please try again.');
      setExceptionModalOpen(false);
      return;
    }
    const updatedWave = result as WaveResponse;
    setWave(updatedWave);
    setExceptionModalOpen(false);
    setScanError(null);
    const nextStep = deriveStep(updatedWave);
    setStep(nextStep);
    if (nextStep.name === 'pick_item') focusInput();
  };

  const handleReleaseRemaining = async () => {
    setReleasing(true);
    try {
      await cancelWave(Number(waveId));
      history.push('/mobile/pick');
    } catch {
      setReleasing(false);
      setReleaseError('Failed to release wave. Please try again.');
    }
  };

  const handleWaveDone = async () => {
    try {
      await completeWave(Number(waveId));
    } catch {
      // best-effort
    }
    history.push('/mobile/pick');
  };

  const renderOrderCard = (item: WaveItem) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary">
          Order
        </Typography>
        <Typography variant="h6">{item.shopifyOrderName}</Typography>
        <Typography variant="body2">{item.customerName}</Typography>
      </CardContent>
    </Card>
  );

  const renderItemCard = (
    item: WaveItem,
    itemIndex: number,
    totalItems: number,
    onException?: () => void,
  ) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {itemIndex} of {totalItems} Items
          </Typography>
          {onException && (
            <IconButton
              size="small"
              onClick={onException}
              sx={{ p: 0.5, ml: 1 }}
              aria-label="Report issue"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="28px"
                viewBox="0 0 24 24"
                width="28px"
                fill="#AAAAAA"
                style={{ flexShrink: 0 }}
              >
                <g>
                  <rect fill="none" height="24" width="24" />
                  <path d="M2.81,2.81L1.39,4.22L7,9.83l0,4.3l-2.6-1.48c-0.17-0.09-0.34-0.14-0.54-0.14c-0.26,0-0.5,0.09-0.7,0.26L2,13.88l6.8,7.18 c0.57,0.6,1.35,0.94,2.18,0.94H17c0.62,0,1.18-0.19,1.66-0.52l1.12,1.12l1.41-1.41L2.81,2.81z M17,20h-6 c-0.39,0-0.64-0.23-0.75-0.36L6.87,16H9l0-4.17l8.14,8.14C17.09,19.98,17.05,20,17,20z M13.83,11H14V3.25C14,2.56,14.56,2,15.25,2 s1.25,0.56,1.25,1.25V11h1V5.25C17.5,4.56,18.06,4,18.75,4S20,4.56,20,5.25v11.92l-2-2V13h-2.17L13.83,11z M13,10.17V2.25 C13,1.56,12.44,1,11.75,1S10.5,1.56,10.5,2.25v5.42L13,10.17z M9.5,6.67V4.25C9.5,3.56,8.94,3,8.25,3c-0.67,0-1.2,0.53-1.24,1.18v0 L9.5,6.67z" />
                </g>
              </svg>
            </IconButton>
          )}
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
          <Typography variant="h5" fontWeight="bold">
            {item.sku}
          </Typography>
          <Typography variant="h5" color="primary">
            {altLocationBinCode ?? item.binLocations[0]?.binCode ?? '—'}
          </Typography>
        </Box>
        {item.imageUrl && (
          <Box mt={1} display="flex" justifyContent="center">
            <img
              src={item.imageUrl}
              alt={item.title ?? item.sku}
              style={{ maxWidth: '100%', maxHeight: 140, objectFit: 'contain' }}
            />
          </Box>
        )}
        {item.title && (
          <Typography variant="body2" color="text.secondary" mt={1}>
            {item.title}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const renderExceptionModal = (item: WaveItem) => {
    const currentBinCode = altLocationBinCode ?? item.binLocations[0]?.binCode;
    const otherLocations = item.binLocations.filter(l => l.binCode !== currentBinCode);

    return (
      <Dialog
        fullScreen
        disableRestoreFocus
        open={exceptionModalOpen}
        onClose={() => setExceptionModalOpen(false)}
      >
        <AppBar sx={{ position: 'relative' }} color="default" elevation={1}>
          <Toolbar>
            <IconButton
              edge="start"
              onClick={() => setExceptionModalOpen(false)}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
              Report an Issue
            </Typography>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {item.sku} — {item.shopifyOrderName}
          </Typography>
          <Typography variant="subtitle2" mb={1}>
            What&apos;s the issue?
          </Typography>
          {(Object.keys(EXCEPTION_LABELS) as FulfillmentLineExceptionType[]).map(type => (
            <Button
              key={type}
              variant={selectedExceptionType === type ? 'contained' : 'outlined'}
              color={selectedExceptionType === type ? 'error' : 'inherit'}
              fullWidth
              sx={{ mb: 1, justifyContent: 'flex-start', textTransform: 'none' }}
              onClick={() => setSelectedExceptionType(type)}
            >
              {EXCEPTION_LABELS[type]}
            </Button>
          ))}
          {selectedExceptionType === 'item_not_found' && otherLocations.length > 0 && (
            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="subtitle2" mb={1}>
                Try a different location:
              </Typography>
              {otherLocations.map(loc => (
                <Button
                  key={loc.binCode}
                  variant="outlined"
                  fullWidth
                  sx={{ mb: 1, justifyContent: 'flex-start', textTransform: 'none' }}
                  onClick={() => {
                    setAltLocationBinCode(loc.binCode);
                    setExceptionModalOpen(false);
                    focusInput();
                  }}
                >
                  {loc.binCode} (qty {loc.quantity})
                </Button>
              ))}
            </Box>
          )}
          <TextField
            label="Notes (optional)"
            multiline
            rows={3}
            fullWidth
            value={exceptionNotes}
            onChange={e => setExceptionNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
          <Button
            variant="contained"
            color="error"
            fullWidth
            disabled={!selectedExceptionType || submittingException}
            onClick={handleSubmitException}
            sx={{ mt: 3 }}
          >
            {submittingException ? 'Submitting…' : 'Submit Exception'}
          </Button>
        </Box>
      </Dialog>
    );
  };

  const renderScanInput = (
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void,
    label: string,
  ) => (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        bgcolor: 'white',
        borderTop: '1px solid #ddd',
        p: 2,
        maxWidth: 480,
        margin: '0 auto',
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
        {label}
      </Typography>
      <input
        ref={inputRef}
        value={scanInput}
        onChange={e => setScanInput(e.target.value)}
        onKeyDown={onKeyDown}
        autoFocus
        style={{
          width: '100%',
          fontSize: 18,
          padding: '10px 12px',
          border: shake ? '2px solid red' : '2px solid #1976d2',
          borderRadius: 6,
          outline: 'none',
          boxSizing: 'border-box',
          animation: shake ? 'shake 0.4s ease' : 'none',
        }}
        placeholder="Scan barcode..."
      />
      {scanError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {scanError}
        </Alert>
      )}
    </Box>
  );

  const scanningOverlay = scanning && (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        bgcolor: 'rgba(255,255,255,0.85)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      <CircularProgress size={56} />
      <Typography variant="h6" color="text.secondary">
        Processing…
      </Typography>
    </Box>
  );

  if (step.name === 'loading') {
    return (
      <MobileLayout>
        <Box display="flex" justifyContent="center" mt={8}>
          <CircularProgress />
        </Box>
      </MobileLayout>
    );
  }

  if (step.name === 'auto_exception') {
    return (
      <MobileLayout>
        <Box display="flex" flexDirection="column" alignItems="center" mt={8} gap={2} px={2}>
          {scanError && <Alert severity="error">{scanError}</Alert>}
          <Alert severity="warning" sx={{ width: '100%' }}>
            No pickable location found for <strong>{step.item.sku}</strong>. This item will be
            marked as an exception and removed from the wave.
          </Alert>
          <Button
            variant="contained"
            color="error"
            fullWidth
            disabled={submittingException}
            onClick={handleAutoException}
          >
            {submittingException ? 'Reporting…' : 'Report Exception & Continue'}
          </Button>
        </Box>
      </MobileLayout>
    );
  }

  if (step.name === 'pick_item') {
    return (
      <MobileLayout>
        {scanningOverlay}
        <Box pb={14} pt={1}>
          {renderOrderCard(step.item)}
          {renderItemCard(step.item, step.itemIndex, step.totalItems, handleOpenException)}
        </Box>
        {renderScanInput(handlePickScan, 'Scan to Pick')}
        {renderExceptionModal(step.item)}
      </MobileLayout>
    );
  }

  if (step.name === 'move_to_qc') {
    return (
      <MobileLayout>
        {scanningOverlay}
        <Box pb={14} pt={1}>
          {renderOrderCard(step.item)}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                {step.itemIndex} of {step.totalItems} Items
              </Typography>
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                <Typography variant="h5" fontWeight="bold">
                  {step.item.sku}
                </Typography>
                <Typography variant="h6" color="secondary">
                  Move to QC
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
        {renderScanInput(handleQcScan, 'Scan to QC Location')}
      </MobileLayout>
    );
  }

  if (step.name === 'item_done') {
    return (
      <MobileLayout>
        <Box display="flex" flexDirection="column" alignItems="center" pt={4} pb={4}>
          <CheckCircleOutlineIcon style={{ fontSize: 80, color: '#4caf50' }} />
          <Typography variant="h5" mt={2} mb={1}>
            Move Complete
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={4}>
            {step.item.sku} moved to QC
          </Typography>
          <Button variant="contained" fullWidth sx={{ mb: 2 }} onClick={handleNextItem}>
            Next Bike
          </Button>
          <Button
            variant="outlined"
            color="error"
            fullWidth
            onClick={() => {
              setReleaseError(null);
              setReleaseConfirmOpen(true);
            }}
          >
            Release Remaining Orders
          </Button>
        </Box>
        <Dialog
          open={releaseConfirmOpen}
          onClose={() => {
            if (!releasing) setReleaseConfirmOpen(false);
          }}
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Release this wave?
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              This cancels the entire wave and releases all remaining orders back to the queue.
              Bikes already moved to QC stay in QC. This can&apos;t be undone.
            </Typography>
            {releaseError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {releaseError}
              </Alert>
            )}
            <Button
              variant="contained"
              fullWidth
              sx={{ mb: 1 }}
              disabled={releasing}
              onClick={() => setReleaseConfirmOpen(false)}
            >
              Keep Picking
            </Button>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              disabled={releasing}
              onClick={handleReleaseRemaining}
            >
              {releasing ? 'Releasing…' : 'Release Wave'}
            </Button>
          </Box>
        </Dialog>
      </MobileLayout>
    );
  }

  // wave_done
  const hasExceptions = step.exceptionCount > 0;
  return (
    <MobileLayout>
      <Box display="flex" flexDirection="column" alignItems="center" pt={4}>
        {hasExceptions ? (
          <ErrorOutlineIcon style={{ fontSize: 80, color: '#ff9800' }} />
        ) : (
          <CheckCircleOutlineIcon style={{ fontSize: 80, color: '#4caf50' }} />
        )}
        <Typography variant="h5" mt={2} mb={1}>
          {hasExceptions ? 'Wave Complete' : 'All Bikes Picked!'}
        </Typography>
        {hasExceptions && (
          <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
            {step.exceptionCount} item{step.exceptionCount > 1 ? 's' : ''} could not be picked —
            exceptions reported
          </Alert>
        )}
        <Card sx={{ width: '100%', mb: 3 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4">{step.completedOrders}</Typography>
            <Typography variant="body2" color="text.secondary">
              Orders Processed
            </Typography>
          </CardContent>
        </Card>
        <Button variant="contained" fullWidth onClick={handleWaveDone}>
          Return Home
        </Button>
      </Box>
    </MobileLayout>
  );
};

export default BikeWavePickFlow;
